import { ChangeEvent, useId, useRef, useState } from "react";
import { ImageOff, Trash2, Upload } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { FieldErrors } from "@/shared/components/field/field";

const svgMediaType = "image/svg+xml";

/**
 * SvgDataUriInput collects an SVG as a base64 data URI.
 *
 * The value is the string that ends up on the entity and is
 * rendered straight into the src attribute of the PDF template, so
 * it has to be inline: the PDF renderer is a separate service and
 * could not fetch a URL. Only SVG is accepted, which is what the
 * backend validates for as well.
 *
 * The file never reaches the backend as a file. It is read into a
 * data URI here and travels as part of the entity, so there is no
 * upload endpoint and no blob to clean up.
 */
export function SvgDataUriInput({
  label,
  description,
  value,
  onValueChange,
  errors = undefined,
  disabled = false,
}: {
  label: string;
  description?: string;
  value: string;
  onValueChange: (value: string) => void;
  errors?: string[];
  disabled?: boolean;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [readError, setReadError] = useState<string | null>(null);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same file twice fires again.
    e.target.value = "";
    if (!file) {
      return;
    }
    if (file.type !== svgMediaType) {
      setReadError(t("Nur SVG-Dateien werden unterstützt."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setReadError(null);
      onValueChange(String(reader.result));
    };
    reader.onerror = () => {
      setReadError(t("Die Datei konnte nicht gelesen werden."));
    };
    reader.readAsDataURL(file);
  };

  return (
    <Field>
      <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
      <div className="flex items-center gap-4">
        <SvgPreview value={value} />
        <div className="flex gap-2">
          <input
            id={inputId}
            ref={inputRef}
            type="file"
            accept={svgMediaType}
            className="hidden"
            disabled={disabled}
            onChange={onFileChange}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            {t("SVG wählen")}
          </Button>
          {value && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => {
                setReadError(null);
                onValueChange("");
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors
        errors={[...(errors ?? []), ...(readError ? [readError] : [])]}
      />
    </Field>
  );
}

function SvgPreview({ value }: { value: string }) {
  if (!value) {
    return (
      <div className="text-muted-foreground flex h-16 w-40 shrink-0 items-center justify-center rounded-md border border-dashed">
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }

  return (
    <div className="flex h-16 w-40 shrink-0 items-center justify-center rounded-md border p-2">
      <img src={value} alt="" className="max-h-full max-w-full" />
    </div>
  );
}
